import { FC, useCallback } from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { NormalFormSelectionField, NormalFormTextField } from '../molecules';
import { Button } from '../atoms';
import { OwnerFilterInput, OrderDirectionOption, PageSizeOption } from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import { orderDirectionMapper, pageSizeMapper } from '@/helpers';

export type OwnerFiltersProps = {
  filters: OwnerFilterInput;
  onFilterChange: (newFilters: OwnerFilterInput) => void;
};

const FiltersWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.border.radius.lg};
  margin-bottom: 2rem;
`;

const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.md};

  @media (min-width: 992px) {
    margin-top: 0;
  }
`;

export const initialOwnerFilters: OwnerFilterInput = {
  orderDirection: OrderDirectionOption.Desc,
  search: null,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
};

export const OwnerFilters: FC<OwnerFiltersProps> = ({ filters, onFilterChange }) => {
  const userLanguage = useUserLanguage();

  const textsData = {
    en: {
      search: 'Search',
      searchPlaceholder: 'Search by name...',
      orderDirection: 'Order by Last Name',
      pageSize: 'Records per page',
      clearFilters: 'Clear Filters',
      all: 'All',
    },
    es: {
      search: 'Buscar',
      searchPlaceholder: 'Buscar por nombre...',
      orderDirection: 'Ordenar por Apellido',
      pageSize: 'Registros por página',
      clearFilters: 'Limpiar Filtros',
      all: 'Todos',
    }
  };

  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const handleFieldChange = useCallback((field: keyof OwnerFilterInput, value: string | null) => {
    onFilterChange({
      ...filters,
      [field]: value === '' ? null : value,
    });
  }, [filters, onFilterChange]);

  const orderDirectionOptions = Object.values(OrderDirectionOption).map(value => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map(value => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  return (
    <FiltersWrapper>
      <Row gutterWidth={16} align="center">
        <Col md={4} sm={12}>
          <NormalFormTextField
            label={texts.search}
            name="search"
            placeholder={texts.searchPlaceholder}
            value={filters.search || ''}
            onChange={(value) => handleFieldChange('search', value)}
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalFormSelectionField<OrderDirectionOption>
            label={texts.orderDirection}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </Col>
        <Col md={3} sm={6}>
          <NormalFormSelectionField<PageSizeOption>
            label={texts.pageSize}
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
            noValueText={texts.all}
          />
        </Col>
        <ButtonCol md={2} sm={12}>
          <Button secondary onClick={() => onFilterChange(initialOwnerFilters)}>
            {texts.clearFilters}
          </Button>
        </ButtonCol>
      </Row>
    </FiltersWrapper>
  );
};

